import { useState, type FormEvent } from 'react'
import type { Session } from './auth'

export interface AuthProps {
  pending: boolean
  error: string | null
  register: (email: string, password: string) => Promise<boolean>
  login: (email: string, password: string) => Promise<boolean>
  clearError: () => void
}

/**
 * Real account creation — DiceTok requires one to post (see auth.ts / netlify
 * function). Register and sign-in share a form; only the submit label and the
 * consent checkbox (required on register only) differ.
 *
 * The Terms/Privacy links open in a new tab rather than navigating away —
 * this form has no draft-saving, so a same-tab link would silently discard
 * whatever the visitor already typed.
 */
export function Auth({ pending, error, register, login, clearError }: AuthProps) {
  const [mode, setMode] = useState<'login' | 'register'>('register')
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [agreed, setAgreed] = useState(false)

  const onSubmit = async (e: FormEvent) => {
    e.preventDefault()
    if (mode === 'register' && !agreed) return
    const ok = mode === 'register' ? await register(email, password) : await login(email, password)
    if (ok) {
      setEmail('')
      setPassword('')
      setAgreed(false)
    }
  }

  const switchMode = (m: 'login' | 'register') => {
    setMode(m)
    clearError()
  }

  return (
    <form className="auth" onSubmit={onSubmit}>
      <div className="authTabs">
        <button
          type="button"
          className={mode === 'register' ? 'active' : ''}
          onClick={() => switchMode('register')}
        >
          Create account
        </button>
        <button type="button" className={mode === 'login' ? 'active' : ''} onClick={() => switchMode('login')}>
          Sign in
        </button>
      </div>

      <input
        type="email"
        value={email}
        onChange={(e) => setEmail(e.target.value)}
        placeholder="Email"
        autoComplete="email"
        required
        aria-label="Email"
      />
      <input
        type="password"
        value={password}
        onChange={(e) => setPassword(e.target.value)}
        placeholder="Password"
        autoComplete={mode === 'register' ? 'new-password' : 'current-password'}
        minLength={8}
        required
        aria-label="Password"
      />

      {mode === 'register' && (
        <label className="consent">
          <input type="checkbox" checked={agreed} onChange={(e) => setAgreed(e.target.checked)} required />
          <span>
            I meet the age requirement in, and agree to, the{' '}
            <a href="/legal/terms" target="_blank" rel="noopener noreferrer">
              Terms
            </a>{' '}
            and{' '}
            <a href="/legal/privacy" target="_blank" rel="noopener noreferrer">
              Privacy Policy
            </a>
            , including that submissions are stored and may be reviewed.
          </span>
        </label>
      )}

      {error && (
        <p className="err" role="alert">
          {error}
        </p>
      )}

      <button type="submit" disabled={pending || (mode === 'register' && !agreed)}>
        {pending ? 'One sec…' : mode === 'register' ? 'Create account' : 'Sign in'}
      </button>
    </form>
  )
}

export function AccountBadge({ session, onLogout }: { session: Session; onLogout: () => void }) {
  return (
    <div className="accountBadge">
      <span>{session.account.email}</span>
      <button type="button" onClick={onLogout}>
        Sign out
      </button>
    </div>
  )
}
